import React, { useMemo } from 'react'
import { Grid, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import moment from 'moment'
import { isNil, keys } from 'lodash'
import Chip from 'common/components/Chip'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import { shortenHumanize } from 'common/utils/DateUtil'
import { UPTIME_GROUPS } from 'supervision/instance/constants/InstanceConstants'

const UPTIME_BAR_HEIGHT = 30
const UPTIME_STATUS_UP = 1
const UPTIME_PERCENT_THRESHOLD = 99

const UptimeTab = ({ uptimeData }) => {
    const uptimeMonitors = useMemo(() => {
        if (!uptimeData?.heartbeatList) return []
        return keys(uptimeData.heartbeatList).map(monitorId => {
            const beats = uptimeData.heartbeatList[monitorId] ?? []
            const raw = uptimeData.uptimeList?.[`${monitorId}_24`]
            const percent = isNil(raw) ? null : (raw <= 1 ? raw * 100 : raw)
            return { monitorId, percent, beats }
        })
    }, [uptimeData])

    if (uptimeMonitors.length === 0) {
        return <Typography>Chargement des données uptime...</Typography>
    }

    return (
        <Stack spacing={3}>
            {uptimeMonitors.map(({ monitorId, percent, beats }) => {
                const oldest = beats[0]
                const oldestLabel = oldest?.time
                    ? shortenHumanize(moment().diff(moment(oldest.time)))
                    : ''
                const chipColor = !isNil(percent) && percent < UPTIME_PERCENT_THRESHOLD
                    ? PASTEL_COLOR.red
                    : PASTEL_COLOR.green
                return (
                    <Grid container key={monitorId} spacing={2} alignItems='center'>
                        <Grid container size={3} spacing={1} alignItems='center'>
                            <Grid>
                                <Chip
                                    sx={{ maxWidth: 'none', width: '100px' }}
                                    label={isNil(percent) ? 'N/A' : `${percent.toFixed(2)}%`}
                                    color={chipColor}
                                />
                            </Grid>
                            <Grid size='auto'>
                                <Typography>{UPTIME_GROUPS[monitorId] || monitorId}</Typography>
                            </Grid>
                        </Grid>
                        <Grid container size='grow' direction='column' spacing={0.5}>
                            <Grid size={12} sx={{ display: 'flex', gap: '2px', height: UPTIME_BAR_HEIGHT }}>
                                {beats.map((beat, i) => (
                                    <div
                                        key={i}
                                        title={beat.msg}
                                        style={{
                                            flex: 1,
                                            height: UPTIME_BAR_HEIGHT,
                                            backgroundColor: beat.status === UPTIME_STATUS_UP ? PASTEL_COLOR.green : PASTEL_COLOR.red,
                                            borderRadius: 2,
                                        }}
                                    />
                                ))}
                            </Grid>
                            <Grid container size={12} justifyContent='space-between'>
                                <Typography variant='caption' color='text.secondary'>{oldestLabel}</Typography>
                                <Typography variant='caption' color='text.secondary'>Maintenant</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                )
            })}
        </Stack>
    )
}

UptimeTab.propTypes = {
    uptimeData: PropTypes.shape({
        heartbeatList: PropTypes.shape({}),
        uptimeList: PropTypes.shape({}),
    }),
}

export default UptimeTab
